import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import { DynamoDBDocumentClient } from '@aws-sdk/lib-dynamodb';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import awsConfig from '../config/aws-config';
import { usageTracker } from './usage-tracker';
import awsAuthService from './aws-auth';

/**
 * Core AWS Service to provide shared, tracked clients
 */

// Singleton client instance
let trackedClient: DynamoDBDocumentClient | null = null;



// ...

const createClient = () => {
    const ddbClient = new DynamoDBClient({
        region: awsConfig.region,
        credentials: async () => {
            // Dynamic credential provider that fetches the latest session
            try {
                // Attempt to get the current session
                const session = await awsAuthService.getSession();
                const idToken = session.getIdToken().getJwtToken();

                const logins = {
                    [`cognito-idp.${awsConfig.region}.amazonaws.com/${awsConfig.cognito.USER_POOL_ID}`]: idToken
                };

                const provider = fromCognitoIdentityPool({
                    client: new CognitoIdentityClient({ region: awsConfig.region }),
                    identityPoolId: awsConfig.cognito.IDENTITY_POOL_ID,
                    logins: logins
                });

                return await provider();
            } catch (error) {
                console.log('No active session, attempting unauthenticated access (which may fail if disabled in Cognito)', error);

                // Fallback to unauthenticated (if enabled in Identity Pool)
                const provider = fromCognitoIdentityPool({
                    client: new CognitoIdentityClient({ region: awsConfig.region }),
                    identityPoolId: awsConfig.cognito.IDENTITY_POOL_ID,
                });

                return await provider();
            }
        }
    });

    const docClient = DynamoDBDocumentClient.from(ddbClient);

    // Add middleware to intercept requests and track usage
    docClient.middlewareStack.add(
        (next, context) => async (args) => {
            // Request consumed capacity
            if (args.input) {
                (args.input as any).ReturnConsumedCapacity = "TOTAL";
            }

            const result = await next(args);

            // Track usage from response
            if (result.output && (result.output as any).ConsumedCapacity) {
                const capacity = (result.output as any).ConsumedCapacity;
                const units = capacity.CapacityUnits || 0;

                // Determine if it was a read or write roughly based on command name
                // This is a heuristic; DynamoDB usually separates them well enough via capacity types if detailed,
                // but TOTAL just gives a number. 
                // Checks context or assume based on command class name? 
                // Middleware context might not have command name easily accessible in this valid middleware signature without generic checks.
                // Simpler: Check if 'Item' or 'Items' is in output (Read) vs input?
                // Actually, DynamoDB write commands (Put, Update, Delete) consume Write Capacity.
                // Read commands (Get, Query, Scan) consume Read Capacity.

                // We'll treat all as generic 'units' but try to categorize if possible.
                // For now, let's just log it. The tracker splits read/write, but we can perhaps infer.
                // Or better: pass a context flag if possible. 

                // Let's guess based on the operation properties
                // This is tricky inside the middleware without the command class name.
                // However, we can track "API Stress" purely as units.
                // For the tracker's distinction, we might default to 'write' if we see write-like attributes, but that's messy.
                // Let's just track it as 'write' if it consumed capacity and wasn't a known read.

                // Refinement: The tracker can accept generic 'units' and maybe we simplify the UI to just 'API Units'.
                // BUT the user asked for "Read and Write".

                // Alternative: The client wrapper below (getUserService etc) knows what it called.
                // But this middleware is automatic.

                // Let's try to detect based on input params
                const input = args.input as any;
                const isRead = input.Key && !input.Item && !input.UpdateExpression && !input.DeleteRequest; // Get
                const isQueryOrScan = input.KeyConditionExpression || input.ScanFilter || (input.TableName && !input.Item && !input.Key);

                if (isRead || isQueryOrScan) {
                    usageTracker.track('read', units);
                } else {
                    usageTracker.track('write', units);
                }
            }

            return result;
        },
        {
            step: "deserialize",
        }
    );

    return docClient;
};

export const getTrackedDynamoDBClient = () => {
    if (!trackedClient) {
        trackedClient = createClient();
    }
    return trackedClient;
};
